// PlanProceduresLayer — render holdings + procedure turns + AIM hold
// entries for procedure-tagged points in the ACTIVE flight plan,
// independent of whether the plate raster is overlaid on the map.
//
// All filtering logic (which holds/PTs to render, dual-purpose
// missed-approach holds, manually-added holds, course-reversal-required
// classifier) lives in src/lib/planHoldFilter.ts so it can be
// unit-tested without the DOM/store dependency surface.
//
// This file only:
//   1. Subscribes to the plan store.
//   2. Extracts + filters holds/PTs via planHoldFilter.
//   3. Pushes the resulting GeoJSON to MapLibre.
//
// Color scheme:
//   - Holdings:        magenta (#ec4899), missed-only dashed purple
//   - Procedure turns: cyan (#06b6d4)
//   - Hold entries:    direct=emerald, teardrop=amber, parallel=red

import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import { usePlanStore } from '../stores/planStore';
import { getMapInstance } from './MapView';
import {
  holdingPatternRing,
  procedureTurnRing,
  holdEntryPath,
} from '../lib/holdingPattern';
import type { ExtendedPlanPoint } from '../lib/procedureExpansion';
import {
  extractHoldsFromRoute,
  extractPtsFromRoute,
  filterHolds,
  filterPts,
  findDualPurposeIafIndex,
  arrivalHeadingAtRouteIndex,
  type HoldRow,
  type PtRow,
} from '../lib/planHoldFilter';

const HOLDS_SOURCE_ID  = 'plan-procedures-holds';
const HOLDS_LINE_LAYER = 'plan-procedures-holds-line';
const HOLDS_LABEL_LAYER = 'plan-procedures-holds-label';
const PTS_SOURCE_ID    = 'plan-procedures-pts';
const PTS_LINE_LAYER   = 'plan-procedures-pts-line';
const PTS_LABEL_LAYER  = 'plan-procedures-pts-label';
const ENTRIES_SOURCE_ID  = 'plan-procedures-entries';
const ENTRIES_LINE_LAYER = 'plan-procedures-entries-line';
const ENTRIES_LABEL_LAYER = 'plan-procedures-entries-label';

function applyHoldsToMap(holds: HoldRow[]): void {
  const map = getMapInstance();
  if (!map) return;
  const features = holds.map((h) => {
    const ring = holdingPatternRing({
      fixLat: h.fixLat,
      fixLon: h.fixLon,
      inboundCourseTrueDeg: h.inboundCourseTrueDeg,
      turnDirection: h.turnDirection,
      legLength: h.holdLeg,
    });
    return {
      type: 'Feature' as const,
      geometry: { type: 'LineString' as const, coordinates: ring },
      properties: {
        fix: h.fixIdent,
        course: Math.round(h.inboundCourseTrueDeg),
        turn: h.turnDirection,
        missed: h.isMissedApproach,
      },
    };
  });
  const fc = { type: 'FeatureCollection' as const, features };
  if (map.getSource(HOLDS_SOURCE_ID)) {
    (map.getSource(HOLDS_SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
    return;
  }
  map.addSource(HOLDS_SOURCE_ID, { type: 'geojson', data: fc });
  map.addLayer({
    id: HOLDS_LINE_LAYER,
    type: 'line',
    source: HOLDS_SOURCE_ID,
    paint: {
      'line-color': [
        'case', ['get', 'missed'],
        '#a855f7',  // purple-500 for missed
        '#ec4899',  // pink-500 for normal holds
      ],
      'line-width': 2.5,
      'line-dasharray': [
        'case', ['get', 'missed'],
        ['literal', [3, 2]],
        ['literal', [1, 0]],
      ],
      'line-opacity': 0.9,
    },
  });
  map.addLayer({
    id: HOLDS_LABEL_LAYER,
    type: 'symbol',
    source: HOLDS_SOURCE_ID,
    layout: {
      'symbol-placement': 'line-center',
      'text-field': ['concat', ['get', 'fix'], ' ', ['get', 'course'], '\u00b0'],
      'text-size': 10,
      'text-offset': [0, 0.6],
    },
    paint: {
      'text-color': '#ec4899',
      'text-halo-color': '#000',
      'text-halo-width': 1.5,
    },
  });
}

function applyPtsToMap(pts: PtRow[]): void {
  const map = getMapInstance();
  if (!map) return;
  const features = pts.map((p) => {
    const ring = procedureTurnRing({
      fixLat: p.fixLat,
      fixLon: p.fixLon,
      inboundCourseTrueDeg: p.inboundCourseTrueDeg,
      turnDirection: p.turnDirection,
    });
    return {
      type: 'Feature' as const,
      geometry: { type: 'LineString' as const, coordinates: ring },
      properties: {
        fix: p.fixIdent,
        course: Math.round(p.inboundCourseTrueDeg),
        turn: p.turnDirection,
      },
    };
  });
  const fc = { type: 'FeatureCollection' as const, features };
  if (map.getSource(PTS_SOURCE_ID)) {
    (map.getSource(PTS_SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
    return;
  }
  map.addSource(PTS_SOURCE_ID, { type: 'geojson', data: fc });
  map.addLayer({
    id: PTS_LINE_LAYER,
    type: 'line',
    source: PTS_SOURCE_ID,
    paint: {
      // PTs render in cyan to visually distinguish them from the magenta
      // hold racetracks. A pilot can tell at a glance whether they're
      // flying a hold or a barbed PT.
      'line-color': '#06b6d4',  // cyan-500
      'line-width': 1.8,
      'line-opacity': 0.9,
    },
  });
  map.addLayer({
    id: PTS_LABEL_LAYER,
    type: 'symbol',
    source: PTS_SOURCE_ID,
    layout: {
      'symbol-placement': 'line',
      'text-field': ['concat', ['get', 'fix'], ' PT'],
      'text-size': 10,
      'text-offset': [0, -0.7],
    },
    paint: {
      'text-color': '#06b6d4',
      'text-halo-color': '#000',
      'text-halo-width': 1.5,
    },
  });
}

function applyEntriesToMap(
  entries: Array<{
    fix: HoldRow;
    arrivalHeadingTrueDeg: number;
  }>,
): void {
  const map = getMapInstance();
  if (!map) return;
  const features = entries.map((e) => {
    const { type, path } = holdEntryPath(
      {
        fixLat: e.fix.fixLat,
        fixLon: e.fix.fixLon,
        inboundCourseTrueDeg: e.fix.inboundCourseTrueDeg,
        turnDirection: e.fix.turnDirection,
        legLength: e.fix.holdLeg,
      },
      { arrivalHeadingTrueDeg: e.arrivalHeadingTrueDeg },
    );
    return {
      type: 'Feature' as const,
      geometry: { type: 'LineString' as const, coordinates: path },
      properties: {
        fix: e.fix.fixIdent,
        entry: type,
        arrivalHeading: Math.round(e.arrivalHeadingTrueDeg),
      },
    };
  });
  const fc = { type: 'FeatureCollection' as const, features };
  if (map.getSource(ENTRIES_SOURCE_ID)) {
    (map.getSource(ENTRIES_SOURCE_ID) as maplibregl.GeoJSONSource).setData(fc);
    return;
  }
  map.addSource(ENTRIES_SOURCE_ID, { type: 'geojson', data: fc });
  map.addLayer({
    id: ENTRIES_LINE_LAYER,
    type: 'line',
    source: ENTRIES_SOURCE_ID,
    paint: {
      'line-color': [
        'match', ['get', 'entry'],
        'direct',   '#34d399',
        'teardrop', '#fbbf24',
        'parallel', '#f87171',
        '#ec4899',
      ],
      'line-width': 2.0,
      'line-dasharray': [2, 2],
      'line-opacity': 0.9,
    },
  });
  map.addLayer({
    id: ENTRIES_LABEL_LAYER,
    type: 'symbol',
    source: ENTRIES_SOURCE_ID,
    layout: {
      'symbol-placement': 'line-center',
      'text-field': ['concat', ['upcase', ['get', 'entry']], ' ENTRY'],
      'text-size': 10,
      'text-offset': [0, -0.8],
    },
    paint: {
      'text-color': [
        'match', ['get', 'entry'],
        'direct',   '#34d399',
        'teardrop', '#fbbf24',
        'parallel', '#f87171',
        '#ec4899',
      ],
      'text-halo-color': '#000',
      'text-halo-width': 1.5,
    },
  });
}

function removeAllFromMap(): void {
  const map = getMapInstance();
  if (!map) return;
  for (const id of [
    ENTRIES_LABEL_LAYER, ENTRIES_LINE_LAYER,
    PTS_LABEL_LAYER, PTS_LINE_LAYER,
    HOLDS_LABEL_LAYER, HOLDS_LINE_LAYER,
  ]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  for (const id of [ENTRIES_SOURCE_ID, PTS_SOURCE_ID, HOLDS_SOURCE_ID]) {
    if (map.getSource(id)) map.removeSource(id);
  }
}

/**
 * No-DOM React component. Subscribes to the plan store and pushes the
 * filtered holdings + procedure-turn + entry geometry to MapLibre on
 * every change.
 */
export function PlanProceduresLayer(): null {
  const route = usePlanStore((s) => s.route);
  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    // The route is stored as PlanPoint[] but procedure-expansion attaches
    // a `procedureTag` field; cast once and operate on the extended type.
    const extRoute = route as ExtendedPlanPoint[];
    // Build a stable signature for the procedure-tagged points so the
    // effect only re-runs when something a procedure-rendering cares
    // about changed.
    const sig = extRoute
      .filter((p) => p.procedureTag)
      .map((p) => `${p.ident}|${p.lat}|${p.lon}|${p.procedureTag?.legType}|${p.procedureTag?.holdInboundCourse}`)
      .join('//');
    if (sig === lastSignatureRef.current) return;
    lastSignatureRef.current = sig;

    const allHolds = extractHoldsFromRoute(extRoute);
    const allPts = extractPtsFromRoute(extRoute);

    // See src/lib/planHoldFilter.ts for the boss's filter rules
    // (manually-added always, missed-approach suppressed unless dual-
    // purpose IAF, course-reversal-required check).
    const { rendered: holds } = filterHolds(allHolds, extRoute);
    const { rendered: pts } = filterPts(allPts, extRoute);

    if (holds.length === 0 && pts.length === 0) {
      removeAllFromMap();
      return;
    }

    applyHoldsToMap(holds);
    applyPtsToMap(pts);

    // Hold entries: only rendered for holds that survived the filter
    // AND whose arrival heading is available from upstream route
    // geometry. For dual-purpose holds (missed-approach hold also
    // serving as a HILPT) the relevant arrival heading is at the IAF,
    // not at the HM downstream of the missed-approach climb.
    const entries: Array<{ fix: HoldRow; arrivalHeadingTrueDeg: number }> = [];
    for (const hold of holds) {
      let evalIdx = hold.routeIndex;
      if (hold.isMissedApproach) {
        const iafIdx = findDualPurposeIafIndex(hold, extRoute);
        if (iafIdx != null) evalIdx = iafIdx;
      }
      const hdg = arrivalHeadingAtRouteIndex(
        extRoute, evalIdx, hold.fixLat, hold.fixLon,
      );
      if (hdg != null) {
        entries.push({ fix: hold, arrivalHeadingTrueDeg: hdg });
      }
    }
    if (entries.length > 0) {
      applyEntriesToMap(entries);
    } else {
      // Clear stale entries from a previous plan.
      const map = getMapInstance();
      if (map?.getSource(ENTRIES_SOURCE_ID)) {
        (map.getSource(ENTRIES_SOURCE_ID) as maplibregl.GeoJSONSource).setData({
          type: 'FeatureCollection', features: [],
        });
      }
    }
  }, [route]);

  useEffect(() => () => removeAllFromMap(), []);

  return null;
}
